// Invitation flow: register (email+password) or accept if already signed-in

import { useEffect, useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import Card from './Card';
import Input from './Input';
import { useFirebase } from './FirebaseContextProvider';
import isEmail from 'validator/lib/isEmail';
import {
  tokenGet,
  userUseToken,
  auth,
  authSendEmailVerification,
} from '../utils/firebase';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import { UsersRoles } from '../utils/enums';
import { getBaseRoute } from './LoginCard';
import './InviteCard.scss';

const initialValues = {
  email: '',
  password: '',
};

const AuthModes = {
  SIGNUP: 'signup',
  LOGIN: 'login',
};

const Views = {
  WRONG: 'wrong',
  APPLIED: 'applied',
  ACCEPTANCE: 'acceptance',
};

const authMsgs = {
  'auth/email-already-in-use':
    'This email is already registered. Try signing in or use another email.',
  'auth/invalid-email': 'Invalid email address',
  'auth/weak-password': 'Password is too weak (min 6 chars)',
  'auth/invalid-credential': 'Invalid email or password',
};

function WrongTokenRole({ tokenRole }) {
  const navigate = useNavigate();

  return (
    <Card header='Wrong invite' className='invite-card'>
      <p className='invitation-text font-16'>
        {'Sorry, but this invitation link is for a '}
        <span className='font-medium'>{tokenRole}</span>
        {`. Please log out first to register as a ${tokenRole}`}
      </p>
      <button
        type='button'
        onClick={() => navigate('/login')}
        className='ok-btn'
      >
        Ok
      </button>
    </Card>
  );
}

function AppliedToken() {
  const navigate = useNavigate();

  return (
    <Card header='Already accepted' className='invite-card'>
      <p className='invitation-text font-16'>
        Sorry, you have accepted the invitation already
      </p>
      <button
        type='button'
        onClick={() => navigate('/login')}
        className='ok-btn'
      >
        Ok
      </button>
    </Card>
  );
}

function AcceptToken({ spinner, inviteText, onAccept }) {
  const navigate = useNavigate();

  return (
    <Card header='You are invited' className='invite-card' spinner={spinner}>
      <p className='invitation-text font-16'>
        {inviteText} Do you want to accept?
      </p>
      <div className='actions'>
        <button
          type='button'
          className='bw'
          onClick={() => navigate('/login')}
          disabled={spinner}
        >
          Cancel
        </button>
        <button type='button' disabled={spinner} onClick={onAccept}>
          Accept
        </button>
      </div>
    </Card>
  );
}

export default function InviteCard({ token }) {
  const [spinner, setSpinner] = useState(true);
  const [pendingRedirect, setPendingRedirect] = useState(false);
  const [pendingApply, setPendingApply] = useState(false);
  const [authMode, setAuthMode] = useState(AuthModes.SIGNUP);
  const [firstRunFlag, setFirstRunFlag] = useState(true);

  // Token meta to show a friendly message
  const [applied, setApplied] = useState(false);
  const [role, setRole] = useState();
  const [teamName, setTeamName] = useState();
  const [tournamentName, setTournamentName] = useState();
  const [userName, setUserName] = useState();

  const { user, isUserResolved } = useFirebase();
  const navigate = useNavigate();

  // Load token details for the invitation banner
  useEffect(() => {
    const load = async () => {
      try {
        setSpinner(true);
        const res = await tokenGet({ id: token, userId: user?.id });
        const { teamName, tournamentName, userName, role, applied } =
          res.data || {};
        setTeamName(teamName);
        setTournamentName(tournamentName);
        setUserName(userName);
        setRole(role);
        setApplied(!!applied);
        setFirstRunFlag(false);
        setSpinner(false);
      } catch (error) {
        toast.error(error?.message || `Error: unable to read token ${token}`);
      }
    };
    // Only user?.id is needed for the call — no reason to wait for the list
    if (isUserResolved && firstRunFlag) load();
  }, [token, user?.id, isUserResolved, firstRunFlag]);

  // Check all the situations with already registered users
  const view = (() => {
    if (!user || !role || pendingApply || user?.role === UsersRoles.NONE)
      return null;
    if (user.role !== role) return Views.WRONG;
    if (applied) return Views.APPLIED;
    return Views.ACCEPTANCE;
  })();

  // Validation schema for sign-up form
  const validationSchema = new Yup.ObjectSchema({
    email: Yup.string()
      .test('is-valid-email', 'Invalid email', (value) => isEmail(value))
      .required('Required'),
    password: Yup.string().required('Required'),
  });

  // Sign-up flow for new users: create account -> apply token -> redirect
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      const { email, password } = values;

      try {
        setSpinner(true);

        // Login case
        if (authMode === AuthModes.LOGIN) {
          await signInWithEmailAndPassword(auth, email, password);
          return;
        }

        // 1) Create Firebase Auth user (auto-signs in on success)
        await createUserWithEmailAndPassword(auth, email, password);

        // 2) Defer applying the token until the user document is visible in context
        setPendingApply(true);

        // 3) Send native Firebase email verification (non-blocking for the rest of the flow)
        try {
          // Continue URL after verification: back to /login
          await authSendEmailVerification({
            email,
            link: `${window.location.origin}/login`,
          });
          toast.success('Verification email sent. Please check your inbox.');
        } catch (e) {
          // Don't break the flow if email couldn't be sent; just notify
          toast.error(
            'Failed to send verification email. You can request it later from your profile.',
          );
        }
      } catch (error) {
        toast.error(
          authMsgs[error?.code] ?? error?.message ?? 'Error occurred',
        );
        setSpinner(false);
      }
    },
  });

  // Apply token once the newly created user is available in context
  useEffect(() => {
    if (!pendingApply) return;
    if (!user) return; // user must be present in context => user doc exists
    (async () => {
      try {
        await userUseToken({ id: user.id, token });
        setPendingRedirect(true);
        // keep spinner on; redirect effect will turn it off when role is present
      } catch (err) {
        toast.error(err?.message ?? 'Failed to accept invitation');
        setSpinner(false);
      } finally {
        setPendingApply(false);
      }
    })();
  }, [pendingApply, user, token]);

  // Redirect only after Firebase users are loaded AND the user has a role
  useEffect(() => {
    if (!pendingRedirect) return;
    // user will be present after createUserWithEmailAndPassword
    if (!user) return;
    // wait until backend finished setting the role and it propagated to the client
    if (user.role === UsersRoles.NONE) return;
    // now safe to stop spinner and redirect straight to the user's profile
    setSpinner(false);
    navigate(getBaseRoute(user), { replace: true });
  }, [pendingRedirect, user, navigate]);

  // Invitation banner text builder
  const inviteText = useMemo(() => {
    if (role) {
      if (teamName)
        return (
          <>
            You are invited to the{' '}
            <span className='font-medium'>{teamName}</span> team as a{' '}
            <span className='font-medium'>{role}</span>.
          </>
        );

      if (tournamentName)
        return (
          <>
            You are invited to the{' '}
            <span className='font-medium'>{tournamentName}</span> tournament as
            a <span className='font-medium'>{role}</span>.
          </>
        );

      if (userName)
        return (
          <>
            You are invited as <span className='font-medium'>{userName}'s</span>{' '}
            <span className='font-medium'>{role}</span>.
          </>
        );

      return (
        <>
          You are invited as a <span className='font-medium'>{role}</span>.
        </>
      );
    }

    return 'Loading invitation details...';
  }, [role, teamName, tournamentName, userName]);

  const toggleAuthMode = (e) => {
    e.preventDefault();
    setAuthMode((m) => {
      if (m === AuthModes.SIGNUP) return AuthModes.LOGIN;
      if (m === AuthModes.LOGIN) return AuthModes.SIGNUP;
      return AuthModes.SIGNUP; // Anyway
    });
  };

  if (view === Views.WRONG) return <WrongTokenRole tokenRole={role} />;
  if (view === Views.APPLIED) return <AppliedToken />;
  if (view === Views.ACCEPTANCE)
    return (
      <AcceptToken
        onAccept={() => {
          setSpinner(true);
          setPendingApply(true);
        }}
        inviteText={inviteText}
        spinner={spinner}
      />
    );

  return (
    <Card header='You are invited' className='invite-card' spinner={spinner}>
      <p className='invitation-text font-16'>
        {inviteText}
        {role &&
          authMode === AuthModes.SIGNUP &&
          " Please provide your email and create a password to register. Don't worry - if you forget password, you'll be able to recover it."}
        {role &&
          authMode === AuthModes.LOGIN &&
          ' Please login to your account to accept the invitation.'}
      </p>
      <form className='invite-form' onSubmit={formik.handleSubmit}>
        <Input
          name='email'
          placeholder='E-mail'
          {...formik.getFieldProps('email')}
          error={
            formik.touched.email && formik.errors.email
              ? formik.errors.email
              : null
          }
        />
        <Input
          name='password'
          placeholder='Password'
          type='password'
          {...formik.getFieldProps('password')}
          error={
            formik.touched.password && formik.errors.password
              ? formik.errors.password
              : null
          }
        />
        <Link
          to='#'
          className='font-medium reset-password'
          onClick={toggleAuthMode}
        >
          {authMode === AuthModes.SIGNUP
            ? 'Already have an account?'
            : 'No account yet? Sign up here'}
        </Link>
        <div className='actions'>
          <button
            type='button'
            className='bw'
            onClick={() => navigate('/login')}
            disabled={spinner}
          >
            Cancel
          </button>
          <button type='submit' disabled={spinner}>
            {authMode === AuthModes.SIGNUP ? 'Create account' : 'Log in'}
          </button>
        </div>
      </form>
    </Card>
  );
}
